import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { Card, Pill, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { Media } from '@/components/Media'
import { FilterBar, FilterChip, FilterSelect } from '@/components/FilterBar'
import { EmptyState } from '@/components/States'
import { PageHeader } from '@/app/layouts/PageHeader'
import { formatMoney, fromMajor } from '@/lib/money'
import {
  AMENITIES,
  CITIES,
  DEFAULT_NIGHTS,
  HOTEL_CASHBACK,
  STAYS,
  type Amenity,
  cityLabel,
} from './travel-data'
import checkout from '@/app/layouts/CheckoutLayout.module.css'

const SORTS = [
  { value: 'recommended', label: 'Most booked' },
  { value: 'price-asc', label: 'Price: low to high' },
  { value: 'price-desc', label: 'Price: high to low' },
  { value: 'score', label: 'Guest score' },
]

const BUDGETS = [
  { value: 'any', label: 'Any price' },
  { value: '100', label: 'Under $100 a night' },
  { value: '150', label: 'Under $150 a night' },
  { value: '200', label: 'Under $200 a night' },
]

export function HotelsScreen() {
  const [params, setParams] = useSearchParams()
  const city = params.get('city') ?? 'all'
  const [query, setQuery] = useState('')
  const [budget, setBudget] = useState('any')
  const [sort, setSort] = useState('recommended')
  const [amenities, setAmenities] = useState<readonly Amenity[]>([])

  /* Functional form: a snapshot of `params` would let two changes in the same
     render clobber each other. */
  const setCity = (value: string) => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current)
        if (value === 'all') next.delete('city')
        else next.set('city', value)
        return next
      },
      { replace: true },
    )
  }

  const dirty =
    city !== 'all' || query !== '' || budget !== 'any' || sort !== 'recommended' || amenities.length > 0

  const reset = () => {
    setQuery('')
    setBudget('any')
    setSort('recommended')
    setAmenities([])
    setCity('all')
  }

  const results = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const cap = budget === 'any' ? Infinity : Number(budget)

    const filtered = STAYS.filter((stay) => {
      if (city !== 'all' && stay.city !== city) return false
      if (stay.nightly > cap) return false
      if (!amenities.every((item) => stay.amenities.includes(item))) return false
      if (!needle) return true
      return (
        stay.name.toLowerCase().includes(needle) ||
        stay.area.toLowerCase().includes(needle) ||
        cityLabel(stay.city).toLowerCase().includes(needle)
      )
    })

    const sorted = [...filtered]
    if (sort === 'price-asc') sorted.sort((a, b) => a.nightly - b.nightly)
    if (sort === 'price-desc') sorted.sort((a, b) => b.nightly - a.nightly)
    if (sort === 'score') sorted.sort((a, b) => b.score - a.score)
    return sorted
  }, [city, query, budget, sort, amenities])

  const toggleAmenity = (item: Amenity) =>
    setAmenities((current) =>
      current.includes(item) ? current.filter((x) => x !== item) : [...current, item],
    )

  return (
    <>
      <PageHeader
        title="Hotels"
        subtitle={`${DEFAULT_NIGHTS} nights · 2 guests · 3% back on every stay, paid in cash`}
      />

      <BentoGrid>
        <BentoSpan span={4}>
          <Card tone="light">
            <FilterBar
              searchLabel="Search by name, neighbourhood or city"
              searchValue={query}
              onSearch={setQuery}
              count={results.length}
              noun="stay"
              dirty={dirty}
              onReset={reset}
              selects={
                <>
                  <FilterSelect
                    label="City"
                    value={city}
                    onChange={setCity}
                    options={[
                      { value: 'all', label: 'All corridors' },
                      ...CITIES.map((item) => ({
                        value: item.id,
                        label: `${item.label}, ${item.country}`,
                      })),
                    ]}
                  />
                  <FilterSelect label="Budget" value={budget} onChange={setBudget} options={BUDGETS} />
                  <FilterSelect label="Sort by" value={sort} onChange={setSort} options={SORTS} />
                </>
              }
              chips={AMENITIES.map((item) => (
                <FilterChip key={item} on={amenities.includes(item)} onClick={() => toggleAmenity(item)}>
                  {item}
                </FilterChip>
              ))}
            />
          </Card>
        </BentoSpan>

        <BentoSpan span={3}>
          <Card tone="light">
            {results.length === 0 ? (
              <EmptyState
                icon="search_off"
                title="Nothing matches all of that"
                body="Try a wider budget, or drop one of the amenities you asked for."
                action={{ label: 'Clear filters', onClick: reset }}
              />
            ) : (
              <Stack gap={0}>
                {results.map((stay) => {
                  const total = fromMajor(stay.nightly * DEFAULT_NIGHTS, 'USD')
                  const back = fromMajor(stay.nightly * DEFAULT_NIGHTS * HOTEL_CASHBACK, 'USD')
                  return (
                    <Link
                      key={stay.slug}
                      to={`/travel/hotels/${stay.slug}`}
                      className={checkout.line}
                      style={{ width: '100%' }}
                    >
                      <Media src={stay.image} alt="" width={64} ratio={1} radius="md" icon="hotel" />
                      <Stack grow gap={0} align="start">
                        <Text variant="caption" weight="semibold">
                          {stay.name}
                        </Text>
                        <Text variant="caption" tone="secondary" truncate>
                          {cityLabel(stay.city)} · {stay.area}
                        </Text>
                      </Stack>
                      <Pill tone="neutral">{stay.score.toFixed(1)}</Pill>
                      <Stack align="end" gap={0} style={{ width: '7rem' }}>
                        <Text variant="caption" weight="semibold" numeric>
                          {formatMoney(total)}
                        </Text>
                        <Text variant="eyebrow" weight="bold" tone="positive" numeric>
                          +{formatMoney(back)} back
                        </Text>
                      </Stack>
                    </Link>
                  )
                })}
              </Stack>
            )}
          </Card>
        </BentoSpan>

        <Card tone="dark">
          <Text variant="caption" weight="semibold" tone="onDark">
            What you pay
          </Text>
          <Stack gap={0} className="pb-mt-3">
            {[
              ['Booking fee', 'None'],
              ['FX fee', '0% · charged in USD'],
              ['Cashback on stays', '3%'],
              ['Free cancellation', 'On most stays'],
            ].map(([label, value]) => (
              <div key={label} className={checkout.line}>
                <Text variant="caption" tone="onDarkMuted">
                  {label}
                </Text>
                <Text variant="caption" weight="semibold" tone="onDark">
                  {value}
                </Text>
              </div>
            ))}
          </Stack>
          <Card.Footer>
            <Text variant="caption" tone="onDarkMuted">
              Cashback is paid into your USD account after check-out, not as points.
            </Text>
          </Card.Footer>
        </Card>
      </BentoGrid>
    </>
  )
}
